import {
  Button,
  Card,
  Dialog,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  IconButton,
} from 'platform/components';
import {VStack, Grid, Text, GridItem, Right, HStack} from 'platform/foundation';

import {useState} from 'react';
import {useSelector} from 'react-redux';

import {selectUserBranchOptions} from '@dms/api/features';
import i18n from '@dms/i18n';

import {Nullish, useBoolean} from 'shared/web';

import {KANBAN_COLUMNS} from '../constants/kanbanColumns';
import {KANBAN_SEGMENTS} from '../constants/kanbanSegments';
import type {KanbanSettings} from '../types/KanbanSettings';
import type {KanbanUser} from '../types/KanbanUser';
import {getKanbanSortOptions} from '../utils/getKanbanSortOptions';
import {getKanbanUsersInBranches} from '../utils/getKanbanUsersInBranches';
import {UserControl} from './UserControl';

interface SettingsProps {
  currentUserId: string;
  users: KanbanUser[] | Nullish;
  settings: KanbanSettings;
  onSave: (settings: KanbanSettings) => void;
}

export function Settings(props: SettingsProps) {
  const branchOptions = useSelector(selectUserBranchOptions);
  const [isDialogOpen, openDialog, closeDialog] = useBoolean(false);
  const [expandedCard, setExpandedCard] = useState<number>(expandedVariants.selectedUsers);

  const handleCloseDialog = () => {
    setExpandedCard(expandedVariants.selectedUsers);
    closeDialog();
  };

  const onSubmit: FormSubmitHandler<KanbanSettings> = (values) =>
    Promise.resolve()
      .then(() => {
        props.onSave({
          ...values,
          selectedUsers: getKanbanUsersInBranches(
            values.selectedUsers,
            values.branchIds,
            props.currentUserId
          ),
        });
      })
      .then(handleCloseDialog);

  return (
    <>
      <IconButton icon="action/settings" priority="default" onClick={openDialog} />
      <Dialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        title={i18n.t('page.opportunitiesKanban.settings.title')}
        size="default"
        scrollBehavior="outside"
        withAdditionalFooter
      >
        <Form<KanbanSettings> defaultValues={props.settings} onSubmit={onSubmit}>
          {(control) => (
            <>
              <VStack spacing={4}>
                <Card
                  variant="inlineGrey"
                  title={i18n.t('page.opportunitiesKanban.settings.selectUsers')}
                  isExpandable
                  isExpanded={expandedCard === expandedVariants.selectedUsers}
                  onExpandButtonClick={() => setExpandedCard(expandedVariants.selectedUsers)}
                >
                  <UserControl
                    control={control}
                    currentUserId={props.currentUserId}
                    users={props.users}
                  />
                </Card>
                <Card
                  variant="inlineGrey"
                  title={i18n.t('page.opportunitiesKanban.settings.selectBranches')}
                  isExpandable
                  isExpanded={expandedCard === expandedVariants.branches}
                  onExpandButtonClick={() => setExpandedCard(expandedVariants.branches)}
                >
                  <FormField
                    type="multiChoice"
                    control={control}
                    name="branchIds"
                    options={branchOptions ?? []}
                    placeholder={i18n.t('entity.branch.labels.selectBranches')}
                    isNotClearable
                    menuInPortal
                  />
                </Card>
                <Card
                  variant="inlineGrey"
                  title={i18n.t('page.opportunitiesKanban.settings.visibleColumns')}
                  isExpandable
                  isExpanded={expandedCard === expandedVariants.columns}
                  onExpandButtonClick={() => setExpandedCard(expandedVariants.columns)}
                >
                  <Grid columns={2} spacing={4}>
                    {KANBAN_SEGMENTS.map((segment) => (
                      <GridItem span={1} key={segment.value}>
                        <VStack spacing={4}>
                          <Text alternative color="primary">
                            {segment.label}
                          </Text>
                          <VStack spacing={2}>
                            {KANBAN_COLUMNS.map((column) => (
                              <FormField
                                key={`${segment.value}-${column.key}`}
                                type="switch"
                                control={control}
                                name={`visibleColumns.0.${segment.value}.${column.key}` as never}
                                label={column.label}
                              />
                            ))}
                          </VStack>
                        </VStack>
                      </GridItem>
                    ))}
                  </Grid>
                </Card>
                <Card
                  variant="inlineGrey"
                  title={i18n.t('general.actions.sortBy')}
                  isExpandable
                  isExpanded={expandedCard === expandedVariants.sortBy}
                  onExpandButtonClick={() => setExpandedCard(expandedVariants.sortBy)}
                >
                  <FormField
                    type="radio"
                    control={control}
                    name="sortBy"
                    direction="column"
                    options={getKanbanSortOptions()}
                  />
                </Card>
              </VStack>
              <DialogFooter>
                <Right>
                  <HStack spacing={2}>
                    <Button
                      title={i18n.t('general.actions.cancel')}
                      variant="secondary"
                      onClick={handleCloseDialog}
                    />
                    <FormButton
                      control={control}
                      title={i18n.t('general.actions.save')}
                      variant="primary"
                      type="submit"
                    />
                  </HStack>
                </Right>
              </DialogFooter>
            </>
          )}
        </Form>
      </Dialog>
    </>
  );
}

const expandedVariants = {
  selectedUsers: 1,
  columns: 2,
  sortBy: 3,
  branches: 4,
};
